(() => {
  const characters = ['starter', 'herb', 'milk', 'burnt', 'mg'];
  const winner = (answers) => {
    if (!Array.isArray(answers) || answers.length !== 5 || answers.some(a => !characters.includes(a))) return null;
    const scores = Object.fromEntries(characters.map(c => [c, 0]));
    answers.forEach(a => { scores[a] += 1; });
    const highest = Math.max(...Object.values(scores));
    return [...answers].reverse().find(a => scores[a] === highest);
  };
  // Values answers break ties first; the remaining selected IDs have canonical priority.
  // Character names never decide priority, and presentation order cannot change an outcome.
  const winnerForResponses = (responses, selectedQuestionIds, answerCharacters) => {
    const ids = [...selectedQuestionIds].sort((a, b) => a.startsWith('vl') !== b.startsWith('vl') ? (a.startsWith('vl') ? -1 : 1) : a.localeCompare(b));
    return winner(ids.reverse().map(id => answerCharacters[responses?.[id]]));
  };
  const shuffled = (values, random = Math.random) => {
    const copy = [...values];
    for (let index = copy.length - 1; index > 0; index -= 1) {
      const swap = Math.floor(random() * (index + 1));
      [copy[index], copy[swap]] = [copy[swap], copy[index]];
    }
    return copy;
  };
  const drawRound = (ids, random = Math.random) => shuffled([
    ...shuffled(ids.filter(id => id.startsWith('sc')), random).slice(0, 2),
    ...shuffled(ids.filter(id => id.startsWith('pk')), random).slice(0, 2),
    ...shuffled(ids.filter(id => id.startsWith('vl')), random).slice(0, 1)
  ], random);
  if (typeof module !== 'undefined' && module.exports) { module.exports = { winner, winnerForResponses, shuffled, drawRound }; return; }
  const root = document.querySelector('[data-toasty-quiz]');
  if (!root || root.dataset.quizReady) return;
  root.dataset.quizReady = 'true';
  const questions = new Map([...root.querySelectorAll('[data-quiz-question-id]')].map(q => [q.dataset.quizQuestionId, q]));
  const questionIds = [...questions.keys()].sort();
  const inputs = new Map([...root.querySelectorAll('[data-quiz-answer-id]')].map(input => [input.dataset.quizAnswerId, input]));
  const answerIds = Object.fromEntries(questionIds.map(id => [id, [...questions.get(id).querySelectorAll('[data-quiz-answer-id]')].map(input => input.dataset.quizAnswerId)]));
  const answerCharacters = Object.fromEntries([...inputs].map(([id, input]) => [id, input.value]));
  const results = [...root.querySelectorAll('[data-quiz-result]')];
  const intro = root.querySelector('[data-quiz-intro]');
  const status = root.querySelector('[data-quiz-status]');
  const progress = root.querySelector('[data-quiz-progress]');
  const dots = [...root.querySelectorAll('[data-quiz-dot]')];
  const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
  const permutationOf = (order, ids) => Array.isArray(order) && order.length === ids.length
    && new Set(order).size === ids.length && order.every(id => ids.includes(id));
  const validAttempt = state => state?.version === 3 && Number.isInteger(state.run) && state.run >= 0
    && Array.isArray(state.questionOrder) && state.questionOrder.length === 5 && new Set(state.questionOrder).size === 5 && ['sc','pk','vl'].every(prefix => state.questionOrder.filter(id => id.startsWith(prefix) && questionIds.includes(id)).length === (prefix === 'vl' ? 1 : 2))
    && questionIds.every(id => permutationOf(state.answerOrders?.[id], answerIds[id]));
  const freshAttempt = run => ({
    version: 3, run, step: -1,
    answers: Object.fromEntries(questionIds.map(id => [id, null])),
    questionOrder: drawRound(questionIds),
    answerOrders: Object.fromEntries(questionIds.map(id => [id, shuffled(answerIds[id])]))
  });
  // Refresh always starts a new draw. History only navigates the current attempt.
  let attempt = freshAttempt(0);
  if (new URLSearchParams(location.search).get('start') === '1') attempt.step = 0;
  const applyOrders = () => {
    attempt.questionOrder.forEach((id, ordinal) => {
      const question = questions.get(id);
      question.parentElement.insertBefore(question, results[0]);
      const list = question.querySelector('.tt-quiz__answers');
      attempt.answerOrders[id].forEach(answerId => list.append(inputs.get(answerId).closest('label')));
      question.querySelector('[data-quiz-next]').textContent = ordinal === attempt.questionOrder.length - 1
        ? root.dataset.resultButtonLabel : root.dataset.nextLabel;
    });
  };
  const limitStep = value => {
    const firstMissing = attempt.questionOrder.findIndex(id => !attempt.answers[id]);
    return Math.max(-1, Math.min(Number.isInteger(value) ? value : -1, firstMissing < 0 ? attempt.questionOrder.length : firstMissing));
  };
  const save = mode => {
    const hash = attempt.step < 0 ? '' : attempt.step === attempt.questionOrder.length ? '#result' : '#question-' + (attempt.step + 1);
    history[mode + 'State']({ ...history.state, toastyQuiz: {
      ...attempt, answers: { ...attempt.answers }, questionOrder: [...attempt.questionOrder],
      answerOrders: Object.fromEntries(questionIds.map(id => [id, [...attempt.answerOrders[id]]]))
    } }, '', location.pathname + location.search + hash);
  };
  const reconcileMotion = () => {
    results.forEach(result => {
      const image = result.querySelector('[data-quiz-image]');
      const stopped = result.hidden || preference.matches;
      image.src = stopped ? image.dataset.stillSrc : image.dataset.animatedSrc;
    });
  };
  const render = moveFocus => {
    attempt.step = limitStep(attempt.step);
    intro.hidden = attempt.step !== -1;
    if(attempt.step === -1) root.removeAttribute('data-quiz-direct-start');
    questions.forEach(question => { question.hidden = true; });
    attempt.questionOrder.forEach((id, ordinal) => {
      const question = questions.get(id);
      question.hidden = ordinal !== attempt.step;
      question.querySelectorAll('[data-quiz-answer-id]').forEach(input => { input.checked = attempt.answers[id] === input.dataset.quizAnswerId; });
      question.querySelector('[data-quiz-next]').disabled = !attempt.answers[id];
    });
    const character = attempt.step === attempt.questionOrder.length ? winnerForResponses(attempt.answers, attempt.questionOrder, answerCharacters) : null;
    results.forEach(result => { result.hidden = result.dataset.quizResult !== character; });
    const result = results.find(r => !r.hidden);
    status.textContent = attempt.step < 0 ? '' : attempt.step < attempt.questionOrder.length
      ? root.dataset.progressLabel.replace('[current]', String(attempt.step + 1))
      : '';
    progress.hidden = attempt.step < 0 || attempt.step >= attempt.questionOrder.length;
    dots.forEach((dot, index) => {
      dot.toggleAttribute('data-filled', index <= attempt.step && !progress.hidden);
      if (index === attempt.step && !progress.hidden) dot.setAttribute('aria-current', 'step');
      else dot.removeAttribute('aria-current');
    });
    reconcileMotion();
    root.dispatchEvent(new CustomEvent('toasty:quiz-result', {detail: {character, run: attempt.run, started: attempt.step >= 0}}));
    if (moveFocus) {
      const heading = attempt.step < 0 ? root.querySelector('[data-quiz-title]') : attempt.step < attempt.questionOrder.length
        ? questions.get(attempt.questionOrder[attempt.step]).querySelector('h2') : result.querySelector('h2');
      heading.focus();
    }
  };
  const go = next => { attempt.step = limitStep(next); save('push'); render(true); };
  root.querySelector('[data-quiz-start]').hidden = false;
  root.querySelector('[data-quiz-fallback]').hidden = true;
  root.addEventListener('change', event => {
    const input = event.target;
    const question = input.closest('[data-quiz-question-id]');
    const id = question?.dataset.quizQuestionId;
    if (input.type !== 'radio' || id !== attempt.questionOrder[attempt.step] || !answerIds[id]?.includes(input.dataset.quizAnswerId)) return;
    attempt.answers[id] = input.dataset.quizAnswerId;
    save('replace');
    question.querySelector('[data-quiz-next]').disabled = false;
  });
  root.addEventListener('click', event => {
    const button = event.target.closest('button');
    if (!button || !root.contains(button)) return;
    if (button.hasAttribute('data-quiz-start')) go(0);
    if (button.hasAttribute('data-quiz-next') && attempt.step >= 0 && attempt.step < attempt.questionOrder.length && attempt.answers[attempt.questionOrder[attempt.step]]) go(attempt.step + 1);
    if (button.hasAttribute('data-quiz-back')) go(attempt.step - 1);
    if (button.hasAttribute('data-quiz-retake')) {
      attempt = freshAttempt(attempt.run + 1); inputs.forEach(input => { input.checked = false; });
      applyOrders(); save('replace'); render(true);
    }
  });
  window.addEventListener('popstate', event => {
    const state = event.state?.toastyQuiz;
    // Keep edits in the current attempt; historical entries only select the displayed step.
    attempt.step = validAttempt(state) && state.run === attempt.run ? limitStep(state.step) : -1;
    save('replace'); render(true);
  });
  preference.addEventListener('change', reconcileMotion);
  applyOrders();
  attempt.step = limitStep(attempt.step);
  save('replace'); render(false);
})();
